import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { buildAbsentMnemonicDescription } from "#build-absent-mnemonic-description";

export type AbsentMnemonicComponent = { gloss: string; grapheme: string };

/**
 * The generated `_part_ + _part_ = term` stand-in shown while a grapheme has no written mnemonic.
 *
 * Both the mnemonic slot and the in-place editor need the exact same string, so it is built
 * once here rather than derived twice from the panel context.
 */
export function useAbsentMnemonicDescription(): {
  componentList: AbsentMnemonicComponent[];
  grapheme: string;
  markdown: string;
} {
  const [grapheme, characterGlossFromCard, components, definitionList] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.back?.trim() ?? "",
    (c) => c.readonlySlice.data.components,
    (c) => c.readonlySlice.data.definitionList,
  );

  const definitionEntryList = definitionList.flatMap(({ entries }) => entries);
  const characterGloss = characterGlossFromCard || definitionEntryList[0]?.trim() || grapheme;
  const componentList = components.map((component) => {
    const componentGrapheme = component.graphemes?.[0]?.glyph ?? "";
    return {
      gloss:
        component.back?.trim() ||
        component.pinyin.gloss?.trim() ||
        componentGrapheme ||
        "component",
      grapheme: componentGrapheme,
    };
  });

  return {
    componentList,
    grapheme,
    markdown: buildAbsentMnemonicDescription(characterGloss, componentList),
  };
}
